import React, { forwardRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import dayjs from "dayjs";
import "react-datepicker/dist/react-datepicker.css";
import "./MembersDateFilter.scss";
import {
  MembersDateFilterInput,
  MembersDateFilterRoot,
} from "./Members.styles";

const formatRangeValue = (start, end) => {
  if (!start) return "";
  const startLabel = dayjs(start).format("MMM D, YYYY");
  if (!end) return `${startLabel} – end`;
  return `${startLabel} – ${dayjs(end).format("MMM D, YYYY")}`;
};

const DatePickerPortal = ({ children }) =>
  typeof document !== "undefined"
    ? createPortal(children, document.body)
    : children;

/** Match Lovable Calendar: fromYear=2015, toYear=current+2 */
const YEAR_FROM = 2015;
const YEAR_TO = new Date().getFullYear() + 2;
const MIN_DATE = new Date(YEAR_FROM, 0, 1);
const MAX_DATE = new Date(YEAR_TO, 11, 31);

const MONTH_OPTIONS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const YEAR_OPTIONS = Array.from(
  { length: YEAR_TO - YEAR_FROM + 1 },
  (_, index) => YEAR_FROM + index
);

/**
 * Lovable dual-month caption: each month has its own month/year selects;
 * prev arrow only on left month, next only on right month.
 *
 * react-datepicker always advances the left (base) month; right = base + 1.
 * So right-panel selects must convert to a base-month update.
 */
const DualMonthHeader = ({
  monthDate,
  customHeaderCount,
  decreaseMonth,
  increaseMonth,
  changeMonth,
  changeYear,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
  singleMonth,
}) => {
  const isLeft = customHeaderCount === 0;
  const isRight = customHeaderCount === 1;
  const showPrev = singleMonth || isLeft;
  const showNext = singleMonth || isRight;

  const handleMonthChange = (nextMonth) => {
    if (isLeft) {
      changeMonth(nextMonth);
      return;
    }
    // Right panel: make this month the second panel → base = nextMonth - 1
    if (nextMonth === 0) {
      changeYear(monthDate.getFullYear() - 1);
      changeMonth(11);
    } else {
      changeMonth(nextMonth - 1);
    }
  };

  const handleYearChange = (nextYear) => {
    if (isLeft) {
      changeYear(nextYear);
      return;
    }
    // Right panel year: if right is January, left is December of previous year
    if (monthDate.getMonth() === 0) {
      changeYear(nextYear - 1);
      changeMonth(11);
    } else {
      changeYear(nextYear);
      changeMonth(monthDate.getMonth() - 1);
    }
  };

  return (
    <div className="membersDateFilter__caption">
      <button
        type="button"
        className="membersDateFilter__nav membersDateFilter__nav--prev"
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        aria-label="Previous month"
        tabIndex={showPrev ? 0 : -1}
        style={showPrev ? undefined : { visibility: "hidden" }}
      />
      <div className="membersDateFilter__dropdowns">
        <select
          className="membersDateFilter__select"
          value={monthDate.getMonth()}
          aria-label="Month"
          onChange={(event) => handleMonthChange(Number(event.target.value))}
        >
          {MONTH_OPTIONS.map((label, monthIndex) => (
            <option key={label} value={monthIndex}>
              {label}
            </option>
          ))}
        </select>
        <select
          className="membersDateFilter__select"
          value={monthDate.getFullYear()}
          aria-label="Year"
          onChange={(event) => handleYearChange(Number(event.target.value))}
        >
          {YEAR_OPTIONS.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>
      <button
        type="button"
        className="membersDateFilter__nav membersDateFilter__nav--next"
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        aria-label="Next month"
        tabIndex={showNext ? 0 : -1}
        style={showNext ? undefined : { visibility: "hidden" }}
      />
    </div>
  );
};

const MembersDateFilter = ({ onRangeChange, clear }) => {
  const [range, setRange] = useState([null, null]);
  const [startDate, endDate] = range;
  const [monthsShown, setMonthsShown] = useState(2);

  useEffect(() => {
    if (clear) {
      setRange([null, null]);
    }
  }, [clear]);

  useEffect(() => {
    const syncMonths = () => {
      setMonthsShown(window.innerWidth < 640 ? 1 : 2);
    };
    syncMonths();
    window.addEventListener("resize", syncMonths);
    return () => window.removeEventListener("resize", syncMonths);
  }, []);

  const DateFilterTrigger = forwardRef(({ onClick }, ref) => (
    <MembersDateFilterInput
      ref={ref}
      type="button"
      onClick={onClick}
      $hasValue={Boolean(startDate)}
    >
      <span className="material-icons" aria-hidden="true">
        event
      </span>
      {formatRangeValue(startDate, endDate) || "Filter by date"}
    </MembersDateFilterInput>
  ));

  DateFilterTrigger.displayName = "DateFilterTrigger";

  return (
    <MembersDateFilterRoot>
      <DatePicker
        selectsRange
        startDate={startDate}
        endDate={endDate}
        monthsShown={monthsShown}
        minDate={MIN_DATE}
        maxDate={MAX_DATE}
        renderCustomHeader={(headerProps) => (
          <DualMonthHeader {...headerProps} singleMonth={monthsShown === 1} />
        )}
        onChange={(update) => {
          const nextRange = update == null ? [null, null] : update;
          setRange(nextRange);
          onRangeChange(nextRange);
        }}
        isClearable
        placeholderText="Filter by date"
        dateFormat="MMM d, yyyy"
        calendarClassName="membersDateFilter__calendar"
        popperClassName="membersDateFilter__popper"
        popperContainer={DatePickerPortal}
        popperPlacement="bottom-end"
        showPopperArrow={false}
        customInput={<DateFilterTrigger />}
      />
    </MembersDateFilterRoot>
  );
};

export default MembersDateFilter;
